import { Box, SxProps, Typography } from '@mui/material';
import { ImageLoader, loadImage } from '@prismamedia/one-components';
import { CircularLoader } from 'components/Loader/Circular';
import { Options } from 'pages/RailroadPage/DisplayOptions';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { FC, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { theme } from 'theme';
import { getRailroadImage } from '../utils';
import { RailroadStatusBar } from './RailroadStatusBar';
import { getColorFromDecimal, getWorkFlow, HalfPage } from './utils';

// eslint-disable-next-line immutable/no-mutation
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export const RailroadIssueHalfPage: FC<{
  page: HalfPage;
  previousPage?: HalfPage;
  nextPage?: HalfPage;
  displayOptions: Options;
  sx?: SxProps;
  previewSx?: SxProps;
  selected?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
}> = ({
  page,
  previousPage,
  nextPage,
  sx,
  previewSx,
  displayOptions,
  selected,
  onClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isPdf = page.filename?.endsWith('.pdf');

  useEffect(() => {
    const loadImageData = async () => {
      setImageSrc(null);
      setLoading(false);

      if (!page.filename) {
        return;
      }

      if (isPdf) {
        setLoading(true);
        const res = await getRailroadImage(page.filename);
        const pdfData = await res.arrayBuffer();
        const pdf = await getDocument({ data: pdfData }).promise;
        const pdfPage = await pdf.getPage(1);
        const viewport = pdfPage.getViewport({ scale: 1 });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;
        // eslint-disable-next-line immutable/no-mutation
        canvas.width = viewport.width;
        // eslint-disable-next-line immutable/no-mutation
        canvas.height = viewport.height;

        await pdfPage.render({ canvasContext: context, viewport }).promise;
        setLoading(false);

        return;
      }

      const src = `${config.API_ONE_TOPIC}${page.filename}`;
      setImageSrc(src);
      await loadImage(src);
    };

    loadImageData().catch((e) => {
      console.error(e);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.filename]);

  const pageIsEven = Number(page.pageNumber || 0) % 2 === 0;

  const workflow = getWorkFlow(page);
  const pageInfo = [
    page.tetiereName?.toUpperCase(),
    page.pageName?.toLowerCase(),
    page.adName?.toLowerCase(),
  ]
    .filter(Boolean)
    .join(' - ');

  if (!page) return null;

  return (
    <Box
      sx={{
        breakInside: 'avoid',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box>
        {displayOptions.displayInfos && (
          <>
            <RailroadStatusBar
              value={page.sheet?.id}
              previousValue={previousPage?.sheet?.id}
              nextValue={nextPage?.sheet?.id}
              pageNumber={page.pageNumber}
              label={page.sheet?.name}
              sx={{ mb: 0.5 }}
              color={getColorFromDecimal(page.sheet?.color)}
            />
            <RailroadStatusBar
              value={page.indesignFilename}
              previousValue={previousPage?.indesignFilename}
              nextValue={nextPage?.indesignFilename}
              pageNumber={page.pageNumber}
              label={page.indesignFilename}
              sx={{ mb: 1 }}
              color={
                page.indesignFilename ? theme.palette.grey[400] : undefined
              }
            />
            <Typography
              sx={{
                width: '100%',
                height: 16,
                textAlign: pageIsEven ? 'left' : 'right',
                fontSize: 10,
                pb: '2px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                textWrap: 'nowrap',
                '@media print': {
                  fontSize: 4,
                  height: 6,
                },
              }}
            >
              {workflow?.label_workflowStep}
            </Typography>
          </>
        )}
      </Box>
      <Box
        onClick={onClick}
        sx={{
          border: selected
            ? `${displayOptions.size * 0.5}px solid ${
                theme.palette.primary.main
              }`
            : displayOptions.displayBorders
            ? `${displayOptions.size * 0.3}px solid ${
                displayOptions.miniature
                  ? theme.palette.grey[200]
                  : getColorFromDecimal(Number(workflow?.workflowStepColor)) ||
                    theme.palette.common.black
              }`
            : undefined,
          borderRightWidth:
            (selected || displayOptions.miniature) && pageIsEven && nextPage
              ? 0
              : undefined,
          borderLeftWidth:
            selected && !pageIsEven && previousPage ? 0 : undefined,
          width: '100%',
          height: '100%',
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: onClick ? 'pointer' : undefined,

          ...previewSx,
          backgroundColor:
            (() => {
              switch (true) {
                case ['ad.', 'ad.fullpage.', 'ad.otherformat'].includes(
                  page.pageType ?? '',
                ):
                  // rose
                  return '#ff94ff';
                case page.pageType?.includes('ad.interpromo.') ?? false:
                  // vert
                  return '#008200';
                case page.pageType?.includes('layout.') ?? false:
                  // bleu
                  return '#cce9ff';
                default:
                  return '#cccccc';
              }
            })() || undefined,
        }}
      >
        {isPdf ? (
          <>
            <Box
              component="canvas"
              ref={canvasRef}
              sx={{
                height: '100%',
                width: '100%',
                opacity:
                  !displayOptions || displayOptions.displayThumbnails
                    ? undefined
                    : 0,
              }}
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularLoader size={'30%'} />
              </Box>
            )}
          </>
        ) : (
          <ImageLoader
            src={imageSrc}
            loaderSize={15}
            fitting="contain"
            sx={{
              width: `100%`,
              opacity: displayOptions.displayThumbnails ? undefined : 0,
            }}
          />
        )}
        {!displayOptions.displayThumbnails && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              width: '100%',
              color: theme.palette.grey[800],
              userSelect: 'none',
              display: 'flex',
              flexDirection: pageIsEven ? 'row' : 'row-reverse',
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: page.child?.displayName ? '50%' : '100%',
                height: '100%',
              }}
            >
              <Typography
                sx={{ fontSize: 11, lineHeight: 1.2, paddingLeft: 0.5 }}
              >
                {pageInfo}
              </Typography>
            </Box>
            {page.child?.displayName && (
              <Box
                sx={{
                  width: '50%',
                  height: '100%',
                  backgroundColor: page.child?.type?.includes('.interpromo')
                    ? '#008200'
                    : '#ff94ff',
                }}
              >
                <Typography
                  sx={{ fontSize: 11, lineHeight: 1.2, paddingLeft: 0.5 }}
                >
                  {page.child.displayName}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {page.advertisementIsCancelled && (
          <Box
            sx={{
              pointerEvents: 'none',
              position: 'absolute',
              inset: 0,
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '145%',
                height: `${Math.max(displayOptions.size * 0.5, 4)}px`,
                transform: 'translate(-50%, -50%) rotate(45deg)',
                transformOrigin: 'center',
                backgroundColor: theme.palette.error.main,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '145%',
                height: `${Math.max(displayOptions.size * 0.5, 4)}px`,
                transform: 'translate(-50%, -50%) rotate(-45deg)',
                transformOrigin: 'center',
                backgroundColor: theme.palette.error.main,
              }}
            />
          </Box>
        )}
      </Box>

      {displayOptions.displayPageNumber && (
        <Typography
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: 15,
            textAlign: pageIsEven ? 'left' : 'right',
            fontSize: 10,
            textOverflow: 'ellipsis',
            color: displayOptions.miniature
              ? theme.palette.grey[500]
              : undefined,
            '@media print': {
              fontSize: 4,
              height: 6,
            },
          }}
        >
          {page.pageNumber}
        </Typography>
      )}
    </Box>
  );
};
